import React from 'react'
import { Location } from 'history'

import {
  getIsForInAppPresentation,
  getRedirectStateFromLocationObject,
  IndexLayoutExtraProps,
  IndexLayoutOldTitle,
  RedirectBadge,
  RouteProps,
  routes,
  SignInForm,
  SocialSignIn,
  useQueryParams,
  resolveRedirectUrl,
} from '@sketch/modules-common'
import { Link, OrderedBreadcrumbs } from '@sketch/components'
import { SignInMutation } from '@sketch/gql-types'

import {
  BottomSection,
  BreadcrumbsWrapper,
  FormContainer,
  Line,
  Paragraph,
} from './SignInView.styles'

const SIGN_IN_CRUMBS = [{ content: 'Sign In' }, { content: 'Create Workspace' }]

interface LocationState {
  from?: Location
  // In some cases, we need to force the users to login with email and password
  // for their SSO account, for example, when accepting a workspace invitation
  hideSSO?: boolean
}

interface BaseSignInViewProps
  extends RouteProps<'SIGN_IN'>,
    IndexLayoutExtraProps {
  location: Location<LocationState>
  email?: string
  onCompleted: (data: SignInMutation) => void
}

export const BaseSignInView = (props: BaseSignInViewProps) => {
  const { location, onCompleted, email } = props

  const { state } = location
  const { from: fromQueryParam } = useQueryParams<'SIGN_IN'>()

  // Resolve to absolute URL so that social/SSO success views can distinguish
  // external redirects (absolute) from internal SPA routes (relative)
  const socialRedirectTo = fromQueryParam
    ? (resolveRedirectUrl(fromQueryParam) ?? undefined)
    : undefined

  const redirectState = state?.from
    ? getRedirectStateFromLocationObject(state.from)
    : null

  const isWorkspaceInvite =
    redirectState && redirectState?.redirectId === 'workspace-invite'

  const showSSOSignInButton = !state?.hideSSO && !isWorkspaceInvite
  const isForInAppPresentation = getIsForInAppPresentation()

  const willContinueToWorkspaceCreation =
    state?.from?.pathname === routes.WORKSPACE_CREATE.create({})

  return (
    <>
      {willContinueToWorkspaceCreation && (
        <BreadcrumbsWrapper>
          <OrderedBreadcrumbs crumbs={SIGN_IN_CRUMBS} currentCrumb={0} />
        </BreadcrumbsWrapper>
      )}

      <IndexLayoutOldTitle>Sign in to Sketch</IndexLayoutOldTitle>

      <FormContainer>
        {state?.from && <RedirectBadge from={state.from} {...state} />}

        <SocialSignIn
          hideSSO={!showSSOSignInButton}
          redirectTo={socialRedirectTo}
        />

        <SignInForm email={email} onCompleted={onCompleted} />
        <Line />
        <BottomSection>
          {!isForInAppPresentation && (
            <Paragraph>
              New to Sketch?{' '}
              <Link
                variant="primary"
                isUnderlined
                to={{
                  pathname: routes.SIGN_UP.create({}),
                  search: fromQueryParam
                    ? `?from=${encodeURIComponent(fromQueryParam)}`
                    : undefined,
                  state,
                }}
              >
                Create an account
              </Link>
            </Paragraph>
          )}
        </BottomSection>
      </FormContainer>
    </>
  )
}
